import React, { useState, useRef, useEffect, useMemo } from "react";
import axios from "../../routers/axios";
import { tokenManager } from "../../routers/api"; // dùng giống student_dashboard
import { CheckCircle, RefreshCw } from 'lucide-react';
import AITeacher from './common/AITeacher';
import QuestionRenderer from '../questions/QuestionRenderer';
import InstructionForSinglePart from '../InstructionForSinglePart';

import JobComparisonQuestion from "../questions/JobComparisonQuestion";
import SynthesisMatrixQuestion from "../questions/SynthesisMatrixQuestion";
import useTimer from "../../hooks/useTimer";



const ReadingSection = ({
  currentScenario,
  currentScenarioData,
  answers,
  setAnswers,
  setReadingFeedback,
  setShowReadingResultsModal,
  setScenario1Results,
  setScenario2Results,
  setReadingQuestions,
  markScenarioComplete,
  isCurrentScenarioComplete,
  testId,
  viewMode = false,
  handleEndTest
}) => {

  const [questions, setQuestions] = useState([]);

  const qNumById = useMemo(() => {
    const map = {};
    (questions || []).forEach(q => {
      map[q.id] = q.question_number ?? 0;
    });
    return map;
  }, [questions]);


  const [readingData, setReadingData] = useState(null);

  // New: Dynamic task instruction states
  const [scenarioTasks, setScenarioTasks] = useState([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [showReadingPassage, setShowReadingPassage] = useState(false);

  useEffect(() => {
    if (!currentScenarioData?.id) return;

    axios
      .get(`/scenarios/tasks/${currentScenarioData.id}?task_type=reading`)
      .then((res) => {
        const sorted = (res.data || []).sort((a, b) => (a.task_order ?? 0) - (b.task_order ?? 0));
        setScenarioTasks(sorted);
        setCurrentTaskIndex(0);
        setShowReadingPassage(sorted.length === 0); // Nếu không có task => hiển thị passage luôn
      })
      .catch((err) => {
        console.error("❌ Error fetching scenario tasks:", err);
        setScenarioTasks([]);
        setShowReadingPassage(true);
      });
  }, [currentScenarioData]);

  const [loading, setLoading] = useState(true);


  const questionsRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // State to track test completion status
  const [submittedFirstTime, setSubmittedFirstTime] = useState(false);
  const [firstSubmissionResults, setFirstSubmissionResults] = useState(null);
  const [secondAttemptAnswers, setSecondAttemptAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [showTask1b, setShowTask1b] = useState(false);
  const [showTest2Scenario1ReadingTask1, setShowTest2Scenario1ReadingTask1] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState("reading_task1");
  const [renderedJobComparisonGroups, setRenderedJobComparisonGroups] = useState(new Set());
  const jobComparisonQuestions =
    questions?.filter((q) => q.question_type === "job_comparison") || [];

  // State to preserve submitted answerss
  const [submittedAnswers, setSubmittedAnswers] = useState({});

  // State to control instruction visibility for Test 1 Scenario 2
  const [showInstructions, setShowInstructions] = useState(false);

  // State to control Task 2 flow for Test 1 Scenario 2
  const [showTask2Instructions, setShowTask2Instructions] = useState(false);
  const [completedTask1, setCompletedTask1] = useState(false);
  const [task1SubmissionResults, setTask1SubmissionResults] = useState(null);

  const [showTask2ReadingInstruction, setShowTask2ReadingInstruction] = useState(false);
  const [showTask2Reading, setShowTask2Reading] = useState(false);



  // NEW: State for Task 2 retry mechanism
  const [task2SubmittedFirstTime, setTask2SubmittedFirstTime] = useState(false);
  const [task2FirstSubmissionResults, setTask2FirstSubmissionResults] = useState(null);
  const [task2SecondAttemptAnswers, setTask2SecondAttemptAnswers] = useState({});
  const [task2SubmittedAnswers, setTask2SubmittedAnswers] = useState({});

  // NEW: State to manage Task 1 retry completion
  const [task1RetryCompleted, setTask1RetryCompleted] = useState(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!currentScenarioData?.id) return;

      try {
        const res = await axios.get(`/questions/list?scenario_id=${currentScenarioData.id}`);
        console.log("[DEBUG questions]", res.data);

        // ✅ Giữ nguyên question_number thật từ DB và sắp xếp đúng thứ tự
        const fetchedQuestions = (res.data || []).sort(
          (a, b) => (a.question_number ?? 0) - (b.question_number ?? 0)
        );

        setQuestions(fetchedQuestions);
        setAnswers({});

        console.log('[Reading] scenarioId =', currentScenarioData?.id);
        console.table(fetchedQuestions.map(q => ({
          id: q.id,
          qnum: q.question_number,
          type: q.question_type
        })));

        const t1 = fetchedQuestions.filter(q => (q.question_number ?? 0) <= 8);
        const t2 = fetchedQuestions.filter(q => (q.question_number ?? 0) >= 9);

        console.log('[Reading] counts => total:', fetchedQuestions.length,
          ' task1:', t1.length, ' task2:', t2.length,
          ' t1 nums:', t1.map(q => q.question_number).join(','),
          ' t2 nums:', t2.map(q => q.question_number).join(',')
        );
      } catch (err) {
        console.error("❌ Error fetching questions:", err);
        setQuestions([]);
      }
    };

    fetchQuestions();
  }, [currentScenarioData]);


  const prevScenarioIdRef = useRef(null);

  useEffect(() => {
    if (!currentScenarioData?.id) return;

    // ⚠️ Chỉ reset khi thực sự chuyển sang scenario khác (không phải refresh)
    if (prevScenarioIdRef.current && prevScenarioIdRef.current !== currentScenarioData.id) {
      console.log("🔄 Switching to new scenario:", currentScenarioData.id);

      setSubmittedFirstTime(false);
      setFirstSubmissionResults(null);
      setSecondAttemptAnswers({});
      setSubmittedAnswers({});
      setShowExplanations({});
      setShowInstructions(false);
      setShowTask2Instructions(false);
      setCompletedTask1(false);
      setTask1SubmissionResults(null);
      setTask2SubmittedFirstTime(false);
      setTask2FirstSubmissionResults(null);
      setTask2SecondAttemptAnswers({});
      setTask2SubmittedAnswers({});
      setTask1RetryCompleted(false);
      setShowTask1b(false);
      setShowTest2Scenario1ReadingTask1(false);
    }

    // ✅ Lưu lại id hiện tại để so sánh lần sau
    prevScenarioIdRef.current = currentScenarioData.id;
  }, [currentScenarioData?.id]);



  useEffect(() => {
    if (!currentScenarioData?.id) return;

    const fetchReadingData = async () => {
      try {
        const res = await axios.get(`/scenarios/reading/${currentScenarioData.id}`);
        setReadingData(res.data);
      } catch (error) {
        console.error("❌ Error fetching reading data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReadingData();
  }, [currentScenarioData?.id]);


  const getUserId = () => {
    try {
      const u = tokenManager?.getUser?.();
      return u?.id ?? u?.userId ?? u?.user_id ?? null;
    } catch {
      return null;
    }
  };

  // Lấy đáp án đúng từ object câu hỏi, ưu tiên correct_answer
  const getCorrectKey = (q) => {
    if (!q) return null;
    return q.correct_answer ?? q.answer ?? q.correct ?? null;
  };

  // So sánh đáp án đã chọn với đáp án đúng (chuẩn hoá in hoa, trim)
  const isCorrectChoice = (q, selected) => {
    const correct = getCorrectKey(q);
    if (correct == null || selected == null) return false;
    return String(selected).trim().toUpperCase() === String(correct).trim().toUpperCase();
  };


  // Gửi kết quả Reading lên BE
  const submitReadingToBE = async (finalResults) => {
    try {
      const user_id = getUserId();
      if (!user_id) {
        console.warn("[Reading] Không lấy được user_id từ token.");
        return null;
      }

      const payload = {
        user_id,
        test_id: Number(testId),                // testId từ props/state
        scenario_id: currentScenarioData?.id,   // ID thực trong DB
        results: finalResults.map(r => ({
          id: r.id,
          selected: r.selected,
          isCorrect: r.isCorrect,
          attempts: r.attempts
        }))
      };

      const res = await axios.post("/test_attempts/submit/reading", payload);
      console.log("[Reading] Saved to BE:", res.data);

      return res.data; // ✅ Trả về attempt object BE trả về
    } catch (e) {
      console.error("[Reading] Save to BE failed:", e);
      return null;
    }
  };


  const getTaskId = () => {
    if (isTest1Scenario2()) {
      if (!completedTask1) return null; // Task 1 chưa complete => instruction chung cho reading part
      else if (shouldShowTask2) return "task2"; // Task 2 đang active => task2 specific
      else return null; // Task 1 đã complete, đang ở retry => vẫn là instruction chung
    }
    return null; // Các scenario khác => instruction chung cho part
  };

  const aiTeacherVideoFileName = currentScenarioData?.instructionReading?.videoFileName;


  // Handle option changes for both first and second attempts
  const handleOptionChange = (questionId, value, isSecondAttempt = false) => {
    const attemptKey = `q${questionId}`;
    const q = questions.find(q => q.id === questionId);

    // 🔥 HÀM FIX — job_comparison luôn cập nhật answers
    if (q?.question_type === "job_comparison") {
      setAnswers(prev => ({ ...prev, [attemptKey]: value }));
      return;
    }

    // ==== logic cũ (để nguyên cho các loại câu hỏi khác) ====
    if (isSecondAttempt) {
      if (isTest1Scenario2() && questionId >= 9 && task2SubmittedFirstTime) {
        setTask2SecondAttemptAnswers(prev => ({ ...prev, [attemptKey]: value }));
      } else {
        setSecondAttemptAnswers(prev => ({ ...prev, [attemptKey]: value }));
      }
    } else {
      if (!submittedFirstTime || (isTest1Scenario2() && completedTask1 && shouldShowTask2)) {
        setAnswers(prev => ({ ...prev, [attemptKey]: value }));
      }
    }
  };

  // Validate answers with support for different question types
  const validateAnswer = (question, selected) => {
    if (!selected) return false;
    if (!question) return false;

    // ✅ Ưu tiên lấy từ correct_answer thay vì answer
    const correct =
      question.correct_answer ??
      question.answer ??
      question.correct ??
      null;

    if (!correct) {
      console.warn("⚠️ Question", question.id, "không có correct_answer trong dữ liệu:", question);
      return false;
    }

    if (typeof selected === "object" && typeof correct === "object") {
      try {
        return JSON.stringify(selected) === JSON.stringify(correct);
      } catch {
        return false;
      }
    }

    const selectedStr = String(selected).trim().toUpperCase();
    const correctStr = String(correct).trim().toUpperCase();

    let isCorrect = false;

    // ==== MATCHING ====
    if (question.question_type === "matching") {
      if (typeof selected === "object" && selected !== null && typeof correct === "object" && correct !== null) {
        isCorrect = JSON.stringify(selected) === JSON.stringify(correct);
      } else {
        isCorrect = selectedStr === correctStr;
      }
    }
    // ==== SYNTHESIS / FILL-IN ====
    else if (
      question.question_type === "synthesis_matrix" ||
      question.question_type === "fill_in_blank" ||
      question.question_type === "classification"
    ) {
      isCorrect = selectedStr === correctStr;
      if (!isCorrect && question.alternativeAnswer)
        isCorrect = selectedStr === String(question.alternativeAnswer).trim().toUpperCase();
      if (!isCorrect && question.alternativeAnswers)
        isCorrect = question.alternativeAnswers.some(
          (alt) => selectedStr === String(alt).trim().toUpperCase()
        );
    }
    // ==== Mặc định ====
    else {
      isCorrect = selectedStr === correctStr;
    }

    // 🟢 Debug log
    console.log(
      `[validateAnswer] Q${question.id}: selected='${selectedStr}', correct='${correctStr}', type='${question.question_type}', result=${isCorrect}`
    );

    return isCorrect;
  };

  // Handle first submission for Task 1 (questions 1-8)
  const handleTask1Submit = () => {
    // Save submitted answers to preserve them
    setSubmittedAnswers({ ...answers });

    // Only evaluate questions 1-8 for Task 1
    const task1Questions = questions.filter(q => (q.question_number ?? 0) <= 8) || [];
    const results = task1Questions.map(q => {
      const selected = answers[`q${q.id}`];
      const isCorrect = validateAnswer(q, selected);

      return {
        id: q.id,
        selected,
        correct: q.correct_answer ?? q.answer ?? q.correct ?? null,
        isCorrect,
        attempts: 1,
        score: isCorrect ? 1 : 0
      };
    });

    setFirstSubmissionResults(results);
    setTask1SubmissionResults(results);
    setSubmittedFirstTime(true);
    setCompletedTask1(true);
    setAnswers({}); // 🔄 Reset để Task 2 bắt đầu với input trống

  };

  // Handle Task 1 retry submission
  const handleTask1RetrySubmit = () => {
    // Calculate final results for Task 1 only
    const finalResults = firstSubmissionResults.map(result => {
      const questionId = result.id;
      const attemptKey = `q${questionId}`;
      const secondAttemptAnswer = secondAttemptAnswers[attemptKey];

      // If correct on first attempt, keep original
      if (result.isCorrect) {
        return result;
      }

      // If attempted second time
      if (secondAttemptAnswer !== undefined) {
        const question = questions?.find(q => q.id === questionId);
        const isSecondCorrect = validateAnswer(question, secondAttemptAnswer);

        return {
          ...result,
          selected: secondAttemptAnswer,
          isCorrect: isSecondCorrect,
          attempts: 2,
          score: isSecondCorrect ? 0.5 : 0
        };
      }

      return { ...result, attempts: 1 }; // No second attempt
    });

    // Update task1SubmissionResults for later use
    setTask1SubmissionResults(finalResults);

    // Đánh dấu Task 1 retry đã hoàn thành
    setTask1RetryCompleted(true);

    // Show Task 2 instructions
    // setShowTask2Instructions(true);
    setShowTask2ReadingInstruction(true); // 👉 hiển thị phần hướng dẫn Task 2 Reading

  };

  // NEW: Handle Task 2 first submission
  const handleTask2FirstSubmit = () => {
    // Save Task 2 submitted answers
    setTask2SubmittedAnswers({ ...answers });

    // Only evaluate questions 9-15 for Task 2
    const task2Questions = questions.filter(q => (q.question_number ?? 0) >= 9) || [];
    const results = task2Questions.map(q => {
      const selected = answers[`q${q.id}`];
      const isCorrect = validateAnswer(q, selected);

      return {
        id: q.id,
        selected,
        correct: q.correct_answer ?? q.answer ?? q.correct ?? null,
        isCorrect,
        attempts: 1,
        score: isCorrect ? 1 : 0
      };
    });

    setTask2FirstSubmissionResults(results);
    setTask2SubmittedFirstTime(true);
  };

  // Handle first submission for full test (non-Test1Scenario2)
  const handleFirstSubmit = () => {
    // Save submitted answers to preserve them
    setSubmittedAnswers({ ...answers });

    const results = questions.map(q => {
      const selected = answers[`q${q.id}`];
      const isCorrect = validateAnswer(q, selected);

      return {
        id: q.id,
        selected,
        correct: q.correct_answer ?? q.answer ?? q.correct ?? null,
        isCorrect,
        attempts: 1,
        score: isCorrect ? 1 : 0
      };
    });

    setFirstSubmissionResults(results);
    setSubmittedFirstTime(true);
  };

  // Handle final submission (includes Task 2 for Test1Scenario2)
  const handleFinalSubmit = async () => {
    let finalResults = [];

    if (isTest1Scenario2()) {
      const task2FinalResults = task2FirstSubmissionResults.map(result => {
        const questionId = result.id;
        const attemptKey = `q${questionId}`;
        const secondAttemptAnswer = task2SecondAttemptAnswers[attemptKey];

        if (result.isCorrect) return result;

        if (secondAttemptAnswer !== undefined) {
          const question = questions.find(q => q.id === questionId);
          const isSecondCorrect = validateAnswer(question, secondAttemptAnswer);
          return {
            ...result,
            selected: secondAttemptAnswer,
            isCorrect: isSecondCorrect,
            attempts: 2,
            score: isSecondCorrect ? 0.5 : 0,
            correct: question?.answer ?? null
          };
        }
        return { ...result, attempts: 1 };
      });

      finalResults = [...(task1SubmissionResults || firstSubmissionResults), ...task2FinalResults];
    } else {
      finalResults = firstSubmissionResults.map(result => {
        const questionId = result.id;
        const attemptKey = `q${questionId}`;
        const question = questions.find(q => q.id === questionId);
        const secondAttemptAnswer = secondAttemptAnswers[attemptKey];

        // 🔥 FIX: xử lý riêng cho Job Comparison
        if (question?.question_type === "job_comparison") {
          // lấy đáp án mới nhất (nếu người dùng chọn lại)
          const latestAnswer = answers[attemptKey] ?? result.selected;

          const isCorrect = validateAnswer(question, latestAnswer);

          return {
            id: questionId,
            selected: latestAnswer,                     // 🔥 luôn dùng đáp án mới
            correct: question.correct_answer ?? null,
            isCorrect,
            attempts: submittedFirstTime ? 2 : 1,
            score: isCorrect ? (submittedFirstTime ? 0.5 : 1) : 0
          };
        }

        // 🔥 CÁC CÂU HỎI KHÁC – GIỮ NGUYÊN LOGIC CŨ
        if (result.isCorrect) return result;

        if (secondAttemptAnswer !== undefined) {
          const isSecondCorrect = validateAnswer(question, secondAttemptAnswer);
          return {
            ...result,
            selected: secondAttemptAnswer,
            isCorrect: isSecondCorrect,
            attempts: 2,
            score: isSecondCorrect ? 0.5 : 0,
            correct: question?.correct_answer ?? null
          };
        }

        return { ...result, attempts: 1 };
      });
    }

    try {
      const payload = {
        user_id: getUserId(),
        test_id: Number(testId),
        scenario_id: currentScenarioData?.id,
        results: finalResults.map(r => ({
          id: r.id,
          selected: r.selected,
          correct: r.correct ?? (questions.find(q => q.id === r.id)?.correct_answer ?? null),
          isCorrect: r.isCorrect,
          attempts: r.attempts,
          score: r.score ?? (r.isCorrect ? (r.attempts === 1 ? 1 : 0.5) : 0)
        }))
      };

      console.log("📤 [Reading Final Submit] Payload:", JSON.stringify(payload, null, 2));

      const res = await axios.post("/test_attempts/reading", payload);

      console.log("📥 [Reading Final Submit] Response từ BE:", res.data);

      // ⚠️ Kiểm tra đúng field trả về
      const attempt = res.data?.data ?? res.data;

      if (!attempt) {
        console.error("❌ Không tìm thấy dữ liệu attempt từ BE:", res.data);
        return;
      }

      // 🟢 Lưu kết quả để hiển thị modal
      const rawFeedback = res.data ?? attempt ?? {};
      const normalizedFeedback = {
        summary:
          rawFeedback.summary ??
          rawFeedback.data?.summary ??
          rawFeedback.answers_json?.summary ??
          {},
        answers:
          rawFeedback.answers ??
          rawFeedback.data?.answers ??
          rawFeedback.answers_json?.answers ??
          [],
      };

      setReadingFeedback(normalizedFeedback);
      setReadingQuestions(questions);
      setShowReadingResultsModal(true);


      console.log("✅ Đã bật modal hiển thị kết quả Reading");

      // Cập nhật trạng thái tổng hợp scenario
      const scenarioResult = {
        scenario: currentScenario,
        readingScore: Number(attempt.reading_score ?? 0),
        readingCompleted: true,
        readingCompletedAt: new Date(attempt.submitted_at)
      };

      if (currentScenario === 1) {
        setScenario1Results(prev => ({ ...prev, ...scenarioResult }));
      } else {
        setScenario2Results(prev => ({ ...prev, ...scenarioResult }));
      }
    } catch (err) {
      console.error("❌ Error submitting final reading:", err.response?.data || err);
    }


  };

  // ✅ Bắt buộc phải có giá trị mặc định để hiển thị ngay lập tức
  const readingDuration = Number(currentScenarioData?.reading_duration ?? 0);
  const remainingTimeFromDB = Math.floor(readingDuration * 60);

  // ✅ Khởi tạo timer từ giá trị có sẵn (không để null)
  const { timeRemaining, formatTime } = useTimer(
    readingDuration > 0 ? readingDuration : 0,
    testId,
    remainingTimeFromDB,   // 👈 KHÔNG null
    viewMode,
    handleEndTest,
    currentScenarioData?.id,
    "reading"
  );

  // ✅ Gửi giá trị ngay lập tức cho NavigationBar nếu có
  useEffect(() => {
    if (typeof window.onTimerUpdate === "function") {
      window.onTimerUpdate(remainingTimeFromDB, formatTime);
    }
  }, [remainingTimeFromDB]);
  // Toggle explanation visibility
  const toggleExplanation = (questionId) => {
    setShowExplanations(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // Get question result from first submission
  const getQuestionResult = (questionId) => {
    // For Task 2 questions in Test 1 Scenario 2
    if (isTest1Scenario2() && questionId >= 9 && task2FirstSubmissionResults) {
      return task2FirstSubmissionResults?.find(r => r.id === questionId);
    }
    // For Task 1 questions or other scenarios
    return firstSubmissionResults?.find(r => r.id === questionId);
  };

  // Handle scroll position tracking
  const handleScroll = () => {
    if (questionsRef.current) {
      scrollPositionRef.current = questionsRef.current.scrollTop;
    }
  };

  // Function to handle showing instructions
  const handleShowInstructions = () => {
    setShowInstructions(true);
  };

  // Function to handle showing Task 2 instructions
  const handleShowTask2Instructions = () => {
    setShowTask2Instructions(true);
  };

  // Function to proceed to Task 2
  const handleProceedToTask2 = () => {
    setShowTask2Instructions(false);
    setAnswers({});
    setSubmittedFirstTime(true);
    setTask2SubmittedFirstTime(false);
    setTask2FirstSubmissionResults(null);
    setTask2SecondAttemptAnswers({});
    setTask2SubmittedAnswers({});
  };

  // New function to skip Task 1 retry and proceed to Task 2
  const handleSkipTask1Retry = () => {
    setTask1RetryCompleted(true);
    setShowTask2Instructions(true);
  };

  // Check if this is Test 1 Scenario 2
  // const isTest1Scenario2 = () => {
  //   return currentScenario === 2 && currentScenarioData?.synthesisInstructions;
  // };
  const isTest1Scenario2 = () => {
    return currentScenario === 2;   // bỏ điều kiện synthesisInstructions
  };

  // Check if data is loaded
  if (!currentScenarioData || !questions?.length) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  // Calculate number of answered questions
  const getAnsweredCount = () => {
    if (isTest1Scenario2()) {
      if (!completedTask1) {
        // Task 1: Count only questions 1-8
        const task1Answers = Object.keys(answers).filter(key => {
          const qId = parseInt(key.replace('q', ''));
          const qNum = qNumById[qId] ?? 0;
          return qNum <= 8;
        });
        return task1Answers.length;
      } else if (shouldShowTask2) {
        // Task 2: Count only questions 9-15
        if (!task2SubmittedFirstTime) {
          const task2Answers = Object.keys(answers).filter(key => {
            const qId = parseInt(key.replace('q', ''));
            const qNum = qNumById[qId] ?? 0;
            return qNum >= 9;
          });
          return task2Answers.length;
        } else {
          // Task 2 retry: count correct + retry attempts
          const incorrectQuestions = task2FirstSubmissionResults.filter(r => !r.isCorrect);
          const secondAttemptCount = incorrectQuestions.filter(q =>
            task2SecondAttemptAnswers[`q${q.id}`] !== undefined
          ).length;
          return task2FirstSubmissionResults.filter(r => r.isCorrect).length + secondAttemptCount;
        }
      } else {
        // Task 1 completed, showing retry: count incorrect questions that have been reattempted
        const incorrectQuestions = firstSubmissionResults.filter(r => !r.isCorrect);
        const secondAttemptCount = incorrectQuestions.filter(q =>
          secondAttemptAnswers[`q${q.id}`] !== undefined
        ).length;
        return firstSubmissionResults.filter(r => r.isCorrect).length + secondAttemptCount;
      }
    } else {
      // Other scenarios
      if (!submittedFirstTime) {
        return Object.keys(answers).length;
      } else {
        // Count incorrect questions that have been reattempted
        const incorrectQuestions = firstSubmissionResults.filter(r => !r.isCorrect);
        const secondAttemptCount = incorrectQuestions.filter(q =>
          secondAttemptAnswers[`q${q.id}`] !== undefined
        ).length;
        return firstSubmissionResults.filter(r => r.isCorrect).length + secondAttemptCount;
      }
    }
  };

  // Get total questions count based on current state
  const getTotalQuestionsCount = () => {
    if (isTest1Scenario2()) {
      if (!completedTask1) {
        // Task 1: only questions 1-8
        return questions?.filter(q => (q.question_number ?? 0) <= 8)?.length || 0;
      } else if (shouldShowTask2) {
        // Task 2: only questions 9-15
        return questions?.filter(q => (q.question_number ?? 0) >= 9)?.length || 0;
      } else {
        // Task 1 retry: only questions 1-8
        return questions?.filter(q => (q.question_number ?? 0) <= 8)?.length || 0;
      }
    }
    return questions?.length || 0;


  };

  // Separate questions into groups
  // Thay vì filter bằng id
  const regularQuestions = questions?.filter(q =>
    q.question_type !== 'synthesis_matrix' && q.question_type !== 'classification' && (q.question_number ?? 0) <= 8
  ) || [];


  const synthesisQuestions = questions?.filter(q =>
    q.question_type === 'synthesis_matrix' || q.question_type === 'classification' || (q.question_number ?? 0) >= 9
  ) || [];


  // For Test 1 Scenario 2, separate Task 1 and Task 2 questions
  const task1Questions = isTest1Scenario2() ? questions?.filter(q => (q.question_number ?? 0) <= 8) : regularQuestions || [];
  const task2Questions = isTest1Scenario2() ? questions?.filter(q => (q.question_number ?? 0) >= 9) : [];

  // Updated renderQuestion function in ReadingSection.js
  // Only showing the key changes - insert this logic into the existing renderQuestion function

  const renderQuestion = (question) => {
    // console.log("renderQuestion called with questions =", questions);
    if (!question) return null;

    // FIXED: Job comparison questions handling
    if (question.question_type === 'job_comparison') {
      // console.log('🎯 Processing job_comparison question:', question.id);

      // Get all job comparison questions
      const jobComparisonQuestions = questions?.filter(q => q.question_type === 'job_comparison');
      // console.log('📊 Found job comparison questions:', jobComparisonQuestions.length);

      // Only render on the FIRST job comparison question
      const firstJobComparisonId = jobComparisonQuestions[0]?.id;

      if (question.id === firstJobComparisonId) {
        // console.log('✅ Rendering JobComparison component for first question ID:', question.id);

        // FIXED: Pass the correct props structure
        const jobComparisonResults = jobComparisonQuestions.map(q => getQuestionResult(q.id)).filter(Boolean);

        const hasIncorrectAnswers = jobComparisonResults.some(r => r && !r.isCorrect);
        const canRetryJobComparison = submittedFirstTime && hasIncorrectAnswers && !shouldShowTask2;

        return (
          <JobComparisonQuestion
            key="job_comparison_group"
            questions={jobComparisonQuestions}
            results={jobComparisonResults}
            currentAnswers={answers}
            submittedFirstTime={submittedFirstTime}
            showExplanation={showExplanations[question.id]}
            canRetry={canRetryJobComparison}
            onAnswerChange={(questionId, value, isSecondAttempt = false) => {
              handleOptionChange(questionId, value, isSecondAttempt);
            }}
            onToggleExplanation={() => toggleExplanation(question.id)}
            showWordBank={true}
          />
        );
      }

      // For other job_comparison questions, return empty div (not null)
      // console.log('⏭️ Skipping render for question ID:', question.id);
      return <div key={question.id} style={{ display: 'none' }}></div>;
    }
    // ✅ Gom nhóm hiển thị duy nhất cho synthesis_matrix
    if (question.question_type === 'synthesis_matrix') {
      // Lấy tất cả câu hỏi thuộc nhóm synthesis_matrix
      const synthesisMatrixQuestions = questions?.filter(
        (q) => q.question_type === 'synthesis_matrix'
      );

      // Chỉ render component 1 lần tại câu đầu tiên
      const firstSynthesisMatrixId = synthesisMatrixQuestions[0]?.id;

      if (question.id === firstSynthesisMatrixId) {
        const synthesisResults = synthesisMatrixQuestions
          .map((q) => getQuestionResult(q.id))
          .filter(Boolean);

        const hasIncorrectAnswers = synthesisResults.some(
          (r) => r && !r.isCorrect
        );
        const canRetrySynthesis =
          submittedFirstTime && hasIncorrectAnswers && !shouldShowTask2;

        return (
          <SynthesisMatrixQuestion
            key="synthesis_matrix_group"
            allQuestions={questions} // truyền toàn bộ danh sách để component lọc ra synthesis_matrix
            questions={synthesisMatrixQuestions}
            results={synthesisResults}
            currentAnswers={answers}
            submittedFirstTime={submittedFirstTime}
            showExplanation={showExplanations[question.id]}
            canRetry={canRetrySynthesis}
            onAnswerChange={(questionId, value, isSecondAttempt = false) => {
              handleOptionChange(questionId, value, isSecondAttempt);
            }}
            onToggleExplanation={() => toggleExplanation(question.id)}
          />
        );
      }

      // Các câu synthesis_matrix còn lại bị ẩn để không lặp bảng
      return <div key={question.id} style={{ display: 'none' }}></div>;
    }

    // Rest of the renderQuestion logic for other question types...
    const result = getQuestionResult(question.id);
    const isFirstAttemptCorrect = result?.isCorrect;

    // Determine retry capability based on context
    let canRetry = false;

    // NEW: True/False questions cannot be retried
    if (question.question_type === 'true_false' || question.question_type === 'true_false_not_given') {
      canRetry = false; // Never allow retry for true/false questions
    } else {
      // Original retry logic for other question types
      if (isTest1Scenario2() && (question.question_number ?? qNumById[question.id] ?? 0) >= 9) {
        // Task 2 questions - can retry after first submission
        canRetry = task2SubmittedFirstTime && !isFirstAttemptCorrect;
      } else if (isTest1Scenario2() && (question.question_number ?? qNumById[question.id] ?? 0) <= 8) {
        // Task 1 questions - can retry after first submission and not showing Task 2
        canRetry = submittedFirstTime && !isFirstAttemptCorrect && !task1RetryCompleted;
      } else {
        // Other scenarios
        canRetry = submittedFirstTime && !isFirstAttemptCorrect && !shouldShowTask2;
      }
    }

    const attemptKey = `q${question.id}`;

    // Determine current answer with proper logic
    let currentAnswer;

    if (canRetry) {
      // If user is in retry mode (not applicable for true/false questions)
      const secondAttemptAnswersSource = (isTest1Scenario2() && question.id >= 9)
        ? task2SecondAttemptAnswers
        : secondAttemptAnswers;
      const submittedAnswersSource = (isTest1Scenario2() && question.id >= 9)
        ? task2SubmittedAnswers
        : submittedAnswers;

      if (secondAttemptAnswersSource[attemptKey] !== undefined) {
        // User has made a retry attempt
        currentAnswer = secondAttemptAnswersSource[attemptKey];
      } else {
        // User hasn't retried yet, show original submitted answer
        currentAnswer = submittedAnswersSource[attemptKey] || answers[attemptKey];
      }
    } else {
      // For first attempt or correct answers or true/false questions
      const relevantSubmittedAnswers = (isTest1Scenario2() && question.id >= 9)
        ? task2SubmittedAnswers
        : submittedAnswers;

      const isSubmitted = (isTest1Scenario2() && question.id >= 9)
        ? task2SubmittedFirstTime
        : submittedFirstTime;

      if (question.question_type === "true_false" || question.question_type === "true_false_not_given") {
        // TRUE/FALSE không retry → luôn hiển thị submitted trước
        currentAnswer = submittedAnswers[attemptKey] ?? answers[attemptKey] ?? null;
      }
      else if (isSubmitted && !task1RetryCompleted) {
        // 🔥 FIX CHÍNH Ở ĐÂY: luôn ưu tiên đáp án mới chọn
        currentAnswer = answers[attemptKey] ?? relevantSubmittedAnswers[attemptKey] ?? null;
      }
      else {
        // working answer
        currentAnswer = answers[attemptKey] !== undefined
          ? answers[attemptKey]
          : (["synthesis_matrix", "fill_in_blank", "classification"].includes(question.question_type)
            ? ""
            : undefined);
      }
    }


    const handleAnswerChange = (value) => {
      // For true/false questions, never allow second attempt
      if (question.question_type === 'true_false' || question.question_type === 'true_false_not_given') {
        handleOptionChange(question.id, value, false);
      } else {
        handleOptionChange(question.id, value, canRetry);
      }
    };

    const handleToggleExplanation = () => {
      toggleExplanation(question.id);
    };

    // For classification questions, pass a function to get current answer for each question ID
    const getCurrentAnswerForClassification = (questionId) => {
      const attemptKey = `q${questionId}`;
      const isTask2Question = isTest1Scenario2() && questionId >= 9;
      const questionType = questions?.find(q => q.id === questionId)?.question_type;

      // True/False questions cannot be retried
      const canRetryLocal = (questionType === 'true_false' || questionType === 'true_false_not_given')
        ? false
        : isTask2Question
          ? (task2SubmittedFirstTime && !getQuestionResult(questionId)?.isCorrect)
          : (submittedFirstTime && !getQuestionResult(questionId)?.isCorrect && !task1RetryCompleted);

      const secondAttemptAnswersSource = isTask2Question ? task2SecondAttemptAnswers : secondAttemptAnswers;
      const submittedAnswersSource = isTask2Question ? task2SubmittedAnswers : submittedAnswers;
      const isSubmitted = isTask2Question ? task2SubmittedFirstTime : submittedFirstTime;

      if (canRetryLocal) {
        return secondAttemptAnswersSource[attemptKey] !== undefined
          ? secondAttemptAnswersSource[attemptKey]
          : submittedAnswersSource[attemptKey] || answers[attemptKey] || '';
      } else {
        if (isSubmitted && !task1RetryCompleted) {
          return submittedAnswersSource[attemptKey] || answers[attemptKey] || '';
        } else {
          return answers[attemptKey] || '';
        }
      }
    };

    // For classification questions, pass the getter function and handle change correctly
    const classificationProps = question.question_type === 'classification' ? {
      currentAnswer: getCurrentAnswerForClassification,
      onAnswerChange: (questionId, value) => {
        const isTask2Question = isTest1Scenario2() && questionId >= 9;
        const questionType = questions.find(q => q.id === questionId)?.question_type;

        // True/False questions cannot be retried
        const canRetryLocal = (questionType === 'true_false' || questionType === 'true_false_not_given')
          ? false
          : isTask2Question
            ? (task2SubmittedFirstTime && !getQuestionResult(questionId)?.isCorrect)
            : (submittedFirstTime && !getQuestionResult(questionId)?.isCorrect && !task1RetryCompleted);

        handleOptionChange(questionId, value, canRetryLocal);
      }
    } : {
      currentAnswer,
      onAnswerChange: handleAnswerChange
    };

    // Determine which submission status to use
    const relevantSubmittedFirstTime = (isTest1Scenario2() && question.id >= 9)
      ? task2SubmittedFirstTime
      : submittedFirstTime;

    // FIXED: For matching questions, pass allAnswers
    const matchingProps = question.question_type === 'matching' ? {
      allAnswers: answers, // Pass current answers so matching questions can see what's already used
    } : {};

    return (
      <QuestionRenderer
        key={`${question.id}-${question.question_type}-${question.question_number}`}
        question={question}
        result={result}
        {...classificationProps}
        // ✅ Dùng chung cho cả Scenario 1 và 2
        submittedFirstTime={
          submittedFirstTime ||
          task2SubmittedFirstTime ||
          firstSubmissionResults?.length > 0 ||
          task2FirstSubmissionResults?.length > 0
        }
        showExplanation={showExplanations[question.id]}
        canRetry={canRetry}
        onToggleExplanation={handleToggleExplanation}
        allQuestions={questions || []}
        {...matchingProps}
        viewMode={viewMode} // ✅ thêm dòng này
      />
    );

  };

  // Check different states for Test 1 Scenario 2
  const shouldShowInstructionsOnly =
    isTest1Scenario2() && !showInstructions && !submittedFirstTime;

  const shouldShowTask2InstructionsOnly =
    isTest1Scenario2() &&
    completedTask1 &&
    task1RetryCompleted &&
    showTask2Instructions;

  const shouldShowTask2 =
    isTest1Scenario2() &&
    completedTask1 &&
    task1RetryCompleted &&
    !showTask2Instructions;

  // console.log("📂 shouldShowInstructionsOnly =", shouldShowInstructionsOnly);
  // console.log("📂 shouldShowTask2InstructionsOnly =", shouldShowTask2InstructionsOnly);
  // console.log("📂 testId =", testId, "currentScenario =", currentScenario);

  return (
    <div className="h-full flex flex-col">
      <AITeacher
        type="reading"
        testId={testId}
        scenarioId={currentScenarioData?.id}  // ✅ id thực trong DB
        scenarioData={currentScenarioData}
        videoFileName={currentScenarioData?.instruction_reading?.videoFileName}
      />


      {/* Show ONLY synthesis instructions initially for Test 1 Scenario 2 */}
      {!showReadingPassage ? (
        <InstructionForSinglePart
          scenarioId={currentScenarioData?.id}
          instructionType="reading"
          onComplete={() => setShowReadingPassage(true)}
        />
      ) : (
        <div className="flex-1 flex flex-col">
          {/* ✅ Task 2 Instruction screen xuất hiện sau khi Task 1 Retry xong */}
          {shouldShowTask2InstructionsOnly && (
            <div className="flex flex-col items-center justify-center py-12 bg-white">
              <InstructionForSinglePart data={currentScenarioData} instructionType="task2" />
              <button
                onClick={handleProceedToTask2}
                className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                Proceed to Task 2
              </button>
            </div>
          )}
          {/* Show Task 2 Instructions component above the reading passage */}
          {shouldShowTask2 &&
            currentTaskIndex === 1 &&                       // 🔥 đảm bảo đúng task 2
            scenarioTasks[1]?.instruction &&               // 🔥 CMS phải có instruction Task 2
            !task2SubmittedFirstTime && (
              <InstructionForSinglePart
                data={currentScenarioData}
                instructionType="task2"
              />
            )}
          {/* Main reading and questions content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Reading Passage - Fixed Left Half */}
            {/* Reading Passage - Left Side */}
            <div className="w-1/2 bg-white border-r border-gray-200 flex flex-col h-full">
              <div className="p-4 border-b bg-gray-50 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  📖&nbsp;
                  <span
                    dangerouslySetInnerHTML={{
                      __html: readingData?.passage_title || readingData?.reading_title || "Reading Passage",
                    }}
                  />
                </h3>

              </div>

              <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                {loading ? (
                  <div className="text-gray-500">Loading passage...</div>
                ) : readingData ? (
                  <div className="prose prose-gray max-w-none">
                    {/* {readingData.reading_description && (
                      <p className="text-gray-700 mb-4">{readingData.reading_description}</p>
                    )}

                    {readingData.reading_instruction && (
                      <div
                        className="tinymce-content mt-4 text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: readingData.reading_instruction }}
                      />
                    )} */}

                    {/* Main passage content */}
                    {readingData.passage_content && (
                      <div
                        className="tinymce-content mt-6 text-gray-800 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: readingData.passage_content }}
                      />
                    )}

                    {/* Reference */}
                    {readingData.passage_reference && (
                      <div className="mt-4 text-sm text-gray-600 italic">
                        <strong>Reference:</strong>{' '}
                        <span dangerouslySetInnerHTML={{ __html: readingData.passage_reference }} />
                      </div>
                    )}

                    {/* Notes */}
                    {readingData.passage_notes && (
                      <div className="mt-4 text-sm text-gray-600">
                        <strong>Notes:</strong>{' '}
                        <span dangerouslySetInnerHTML={{ __html: readingData.passage_notes }} />
                      </div>
                    )}

                    {/* Marking Rubric */}
                    {readingData.marking_rubric && (() => {
                      // Try to parse as JSON array (raw DB data), fallback to HTML string (TinyMCE)
                      let rubricItems = null;
                      try {
                        const parsed = typeof readingData.marking_rubric === 'string'
                          ? JSON.parse(readingData.marking_rubric)
                          : readingData.marking_rubric;
                        if (Array.isArray(parsed)) rubricItems = parsed;
                      } catch {
                        // Not JSON — will use dangerouslySetInnerHTML below
                      }

                      return (
                        <div className="mt-6 border border-blue-200 rounded-lg overflow-hidden">
                          <div className="bg-blue-600 px-4 py-2">
                            <h4 className="font-semibold text-white text-sm tracking-wide uppercase">
                              📋 Marking Rubric
                            </h4>
                          </div>

                          {rubricItems ? (
                            // ✅ JSON array from DB → render as styled table
                            <div className="divide-y divide-gray-100">
                              {rubricItems.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-3 px-4 py-3 bg-white hover:bg-blue-50 transition-colors">
                                  <div className="flex-shrink-0 mt-0.5">
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                                      {idx + 1}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-semibold text-gray-800 text-sm">
                                        {item.criteria}
                                      </span>
                                      {item.weight && (
                                        <span className="inline-block px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded-full">
                                          {item.weight}
                                        </span>
                                      )}
                                    </div>
                                    {item.description && (
                                      <p className="text-gray-600 text-sm mt-0.5 leading-snug">
                                        {item.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            // ✅ HTML string from TinyMCE → render as HTML
                            <div
                              className="p-4 bg-white text-gray-700 tinymce-content"
                              dangerouslySetInnerHTML={{ __html: readingData.marking_rubric }}
                            />
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-gray-500 italic">No reading passage available.</div>
                )}
              </div>
            </div>


            {/* Questions - Fixed Right Half */}
            <div className="w-1/2 bg-white flex flex-col h-full">
              <div className="p-4 border-b bg-gray-50 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  🔍 {isTest1Scenario2() && shouldShowTask2 ? 'Task 2 - Synthesis Matrix' : 'Questions'} ({getAnsweredCount()}/{getTotalQuestionsCount()} answered)
                </h3>
                {/* Show results when appropriate */}
                {(submittedFirstTime && firstSubmissionResults && !task1RetryCompleted) ||
                  (task2SubmittedFirstTime && task2FirstSubmissionResults && shouldShowTask2) ? (
                  <div className="mt-2 flex items-center space-x-4 text-sm">
                    <span className="text-green-600">
                      ✓ Correct: {(() => {
                        const results = shouldShowTask2 && task2FirstSubmissionResults
                          ? task2FirstSubmissionResults
                          : firstSubmissionResults;
                        return results.filter(r => r.isCorrect).length;
                      })()}
                    </span>
                    <span className="text-red-600">
                      ✗ Incorrect: {(() => {
                        const results = shouldShowTask2 && task2FirstSubmissionResults
                          ? task2FirstSubmissionResults
                          : firstSubmissionResults;
                        return results.filter(r => !r.isCorrect).length;
                      })()}
                    </span>
                  </div>
                ) : null}
              </div>

              <div
                ref={questionsRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto p-6"
                style={{ maxHeight: 'calc(100vh - 280px)' }}
              >
                <div className="space-y-6">
                  {/* ✅ Hiển thị danh sách câu hỏi (bao gồm synthesis_matrix thông qua QuestionRenderer) */}
                  {(() => {
                    // Lấy danh sách câu hỏi đang hiển thị (task hoặc toàn bộ)
                    const currentQuestions = (
                      isTest1Scenario2()
                        ? (shouldShowTask2 ? task2Questions : task1Questions)
                        : questions
                    ).filter(
                      (q) => q.question_number !== null && q.question_number !== undefined
                    );

                    if (!currentQuestions || currentQuestions.length === 0) return null;

                    // ✅ Gom nhóm theo question_type
                    const groupedQuestions = currentQuestions.reduce((acc, q) => {
                      if (!acc[q.question_type]) acc[q.question_type] = [];
                      acc[q.question_type].push(q);
                      return acc;
                    }, {});

                    // ✅ Sắp xếp từng nhóm theo question_number
                    Object.keys(groupedQuestions).forEach((type) => {
                      groupedQuestions[type].sort(
                        (a, b) => (a.question_number ?? 0) - (b.question_number ?? 0)
                      );
                    });

                    // ✅ Tạo danh sách nhóm theo thứ tự question_number tổng thể
                    //    để hiển thị theo đúng thứ tự giữa các loại khác nhau
                    const orderedGroups = Object.values(groupedQuestions)
                      .flat()
                      .sort((a, b) => (a.question_number ?? 0) - (b.question_number ?? 0));

                    // ✅ Render từng câu theo đúng thứ tự tổng thể
                    return orderedGroups.map((question) => (
                      <div
                        key={`${question.id}-${question.question_type}-${question.question_number}`}
                      >
                        {renderQuestion(question)}
                      </div>
                    ));
                  })()}
                </div>

              </div>

              {/* Submit Button */}
              <div className="p-4 border-t bg-white flex-shrink-0">
                {/* ✅ Bỏ hiển thị thông báo View Only */}
                {/* For Test 1 Scenario 2 - Different buttons based on state */}
                {isTest1Scenario2() ? (
                  <>
                    {!completedTask1 ? (
                      // Task 1 Submit Button
                      <button
                        onClick={!viewMode ? handleTask1Submit : undefined}
                        disabled={
                          viewMode ||
                          Object.keys(answers).filter((key) => {
                            const qId = parseInt(key.replace("q", ""));
                            const qNum = qNumById[qId] ?? 0;
                            return qNum <= 8;
                          }).length < task1Questions.length
                        }
                        className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${viewMode
                          ? "bg-gray-400 text-white cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span>Submit Task 1</span>
                      </button>
                    ) : shouldShowTask2 ? (
                      !task2SubmittedFirstTime ? (
                        // Task 2 First Submit Button
                        <button
                          onClick={!viewMode ? handleTask2FirstSubmit : undefined}
                          disabled={
                            viewMode ||
                            Object.keys(answers).filter((key) => {
                              const qId = parseInt(key.replace("q", ""));
                              return qId >= 9;
                            }).length < task2Questions.length
                          }
                          className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${viewMode
                            ? "bg-gray-400 text-white cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                        >
                          <CheckCircle className="w-5 h-5" />
                          <span>Submit Task 2 (Attempt 1)</span>
                        </button>
                      ) : (
                        // Task 2 Final Submit Button
                        <button
                          onClick={!viewMode ? handleFinalSubmit : undefined}
                          disabled={viewMode}
                          className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${viewMode
                            ? "bg-gray-400 text-white cursor-not-allowed"
                            : "bg-green-600 text-white hover:bg-green-700"
                            }`}
                        >
                          <RefreshCw className="w-5 h-5" />
                          <span>Final Submit Task 2</span>
                        </button>
                      )
                    ) : (
                      // Task 1 Retry Buttons - Hiển thị 2 options
                      <div className="space-y-2">
                        <button
                          onClick={!viewMode ? handleTask1RetrySubmit : undefined}
                          disabled={viewMode}
                          className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${viewMode
                            ? "bg-gray-400 text-white cursor-not-allowed"
                            : "bg-orange-600 text-white hover:bg-orange-700"
                            }`}
                        >
                          <RefreshCw className="w-5 h-5" />
                          <span>Submit Task 1 Retry & Proceed to Task 2</span>
                        </button>

                        <button
                          onClick={!viewMode ? handleSkipTask1Retry : undefined}
                          disabled={viewMode}
                          className={`w-full py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors text-sm ${viewMode
                            ? "bg-gray-300 text-white cursor-not-allowed"
                            : "bg-gray-600 text-white hover:bg-gray-700"
                            }`}
                        >
                          <span>Skip Retry & Proceed to Task 2</span>
                        </button>
                      </div>
                    )}

                    {/* Help texts giữ nguyên */}
                    {!completedTask1 &&
                      Object.keys(answers).filter((key) => {
                        const qId = parseInt(key.replace("q", ""));
                        const qNum = qNumById[qId] ?? 0;
                        return qNum <= 8;
                      }).length < task1Questions.length && (
                        <p className="text-sm text-gray-500 text-center mt-2">
                          Please answer all Task 1 questions before submitting
                        </p>
                      )}

                    {completedTask1 && !task1RetryCompleted && (
                      <div className="text-sm text-center mt-2 space-y-1">
                        <p className="text-blue-600">
                          Click "Explanation" to view explanations. You can retry incorrect answers or skip to Task 2.
                        </p>
                        <p className="text-gray-500">
                          Note: Retry attempts will receive half credit if correct.
                        </p>
                      </div>
                    )}

                    {shouldShowTask2 &&
                      !task2SubmittedFirstTime &&
                      Object.keys(answers).filter((key) => {
                        const qId = parseInt(key.replace("q", ""));
                        return qId >= 9;
                      }).length < task2Questions.length && (
                        <p className="text-sm text-gray-500 text-center mt-2">
                          Please answer all Task 2 questions before submitting
                        </p>
                      )}

                    {shouldShowTask2 && task2SubmittedFirstTime && (
                      <p className="text-sm text-blue-600 text-center mt-2">
                        Click "Explanation" to view explanations. Retry incorrect answers and click "Final Submit"
                      </p>
                    )}
                  </>
                ) : (
                  // For other scenarios - Original logic
                  <>
                    {!submittedFirstTime ? (
                      <button
                        onClick={!viewMode ? handleFirstSubmit : undefined}
                        disabled={viewMode || Object.keys(answers).length < questions?.length}
                        className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${viewMode
                          ? "bg-gray-400 text-white cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span>Submit Reading (Attempt 1)</span>
                      </button>
                    ) : (
                      <button
                        onClick={!viewMode ? handleFinalSubmit : undefined}
                        disabled={viewMode}
                        className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${viewMode
                          ? "bg-gray-400 text-white cursor-not-allowed"
                          : "bg-green-600 text-white hover:bg-green-700"
                          }`}
                      >
                        <RefreshCw className="w-5 h-5" />
                        <span>Final Submit Reading</span>
                      </button>
                    )}

                    {!submittedFirstTime && Object.keys(answers).length < questions?.length && (
                      <p className="text-sm text-gray-500 text-center mt-2">
                        Please answer all questions before submitting
                      </p>
                    )}

                    {submittedFirstTime && (
                      <p className="text-sm text-blue-600 text-center mt-2">
                        Click "Explanation" to view explanations. Retry incorrect answers and click "Final Submit"
                      </p>
                    )}
                  </>
                )}
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadingSection;